import { Alert, Box, Button, CircularProgress, Stack, Typography } from '@mui/material';
import { Download } from '@mui/icons-material';
import { useCallback, useEffect, useMemo, useState } from 'react';
import type { Photo } from '../../../types/photo';
import { rockBtsPhotoCategories, type RockBtsPhotoCategoryId } from '../../../types/rockBtsPhoto';
import * as photoService from '../../../services/photoService';
import { usePermissions } from '../../../hooks/usePermissions';
import { TriaxialPhotoCategorySection } from '../photos/TriaxialPhotoCategory';
import { CropDialog } from '../photos/CropDialog';
import { BTS_DISC_ASPECT_PRESETS, BTS_REPORT_ASPECT } from '../../../types/photo';
import type { SlotUploadState } from '../photos/PhotoSlotComponents';

interface Props {
  workOrderDetailKey: string;
  specimenCount: number;
}

interface CropState {
  imageUrl: string;
  fileName: string;
  photoKey: string;
  fullSelection?: boolean;
}

/**
 * Rock BTS Photos tab — before/after per specimen (REQ-03; report page 2).
 * Same free-form category model as the triaxial photos tab; the category
 * list adapts to the test's specimen count. The report prints the first
 * photo of each category.
 */
export const RockBTSPhotosTab = ({ workOrderDetailKey, specimenCount }: Props) => {
  const { canEditTestData } = usePermissions();
  const [photos, setPhotos] = useState<Photo[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [uploadByCategory, setUploadByCategory] = useState<Partial<Record<RockBtsPhotoCategoryId, SlotUploadState>>>({});
  const [cropState, setCropState] = useState<CropState | null>(null);

  const categories = useMemo(() => rockBtsPhotoCategories(specimenCount), [specimenCount]);

  const fetchPhotos = useCallback(async () => {
    try {
      setError(null);
      const data = await photoService.getPhotos(workOrderDetailKey);
      setPhotos(data);
    } catch (err) {
      setError((err as Error).message || 'Failed to load photos');
    } finally {
      setLoading(false);
    }
  }, [workOrderDetailKey]);

  useEffect(() => { fetchPhotos(); }, [fetchPhotos]);

  /** Photos grouped & sorted by category. */
  const photosByCategory = useMemo(() => {
    const byCat: Record<RockBtsPhotoCategoryId, Photo[]> = {
      'bts-s1-before': [], 'bts-s1-after': [], 'bts-s2-before': [], 'bts-s2-after': [],
    };
    for (const p of photos) {
      const cat = p.Stage as RockBtsPhotoCategoryId;
      if (cat in byCat && p.UploadStatus === 'complete') byCat[cat].push(p);
    }
    (Object.keys(byCat) as RockBtsPhotoCategoryId[]).forEach((k) => {
      byCat[k].sort((a, b) => Number(a.Position || 0) - Number(b.Position || 0));
    });
    return byCat;
  }, [photos]);

  const totalCount = categories.reduce((sum, c) => sum + photosByCategory[c.id].length, 0);

  const handleAddPhoto = useCallback(async (category: RockBtsPhotoCategoryId, file: File) => {
    const existing = photosByCategory[category];
    const usedPositions = new Set(existing.map((p) => Number(p.Position || 0)));
    let nextPos = 1;
    while (usedPositions.has(nextPos)) nextPos += 1;
    const position = String(nextPos);

    setUploadByCategory((s) => ({ ...s, [category]: { progress: 0, status: 'uploading', step: 'uploading' } }));
    try {
      const SUB: Record<string, number> = { uploading: 10, processing: 50, saving: 85, done: 100 };
      await photoService.slotUpload(workOrderDetailKey, file, category, position, 'average', (evt) => {
        setUploadByCategory((s) => ({
          ...s,
          [category]: { progress: SUB[evt.type] ?? 0, status: 'uploading', step: evt.type },
        }));
      });
      setUploadByCategory((s) => ({ ...s, [category]: undefined }));
      await fetchPhotos();
    } catch (err) {
      setError((err as Error)?.message || `Failed to add photo to ${category}`);
      setUploadByCategory((s) => ({ ...s, [category]: { progress: 0, status: 'error' } }));
    }
  }, [workOrderDetailKey, photosByCategory, fetchPhotos]);

  const handleRecrop = useCallback((photo: Photo) => {
    setCropState({
      imageUrl: photo.originalImageUrl || photo.imageUrl,
      fileName: photo.FileName,
      photoKey: photo.PhotoKey,
    });
  }, []);

  const handleEditCropped = useCallback((photo: Photo) => {
    setCropState({
      imageUrl: photo.imageUrl,
      fileName: photo.FileName,
      photoKey: photo.PhotoKey,
      fullSelection: true,
    });
  }, []);

  const handleCropConfirm = useCallback(async (croppedFile: File) => {
    if (!cropState) return;
    const { photoKey, imageUrl } = cropState;
    setCropState(null);
    if (imageUrl.startsWith('blob:')) URL.revokeObjectURL(imageUrl);
    try {
      const { uploadUrl } = await photoService.requestRecropUrl(photoKey);
      await photoService.uploadToBlob(uploadUrl, croppedFile);
      await photoService.confirmRecrop(photoKey);
      await fetchPhotos();
    } catch (err) {
      setError((err as Error)?.message || 'Recrop failed');
    }
  }, [cropState, fetchPhotos]);

  const handleCropCancel = useCallback(() => {
    if (cropState?.imageUrl.startsWith('blob:')) URL.revokeObjectURL(cropState.imageUrl);
    setCropState(null);
  }, [cropState]);

  const handleDelete = useCallback(async (photoKey: string) => {
    try {
      await photoService.deletePhoto(photoKey);
      await fetchPhotos();
    } catch (err) {
      setError((err as Error)?.message || 'Failed to delete photo');
    }
  }, [fetchPhotos]);

  const handleUpdateComment = useCallback(async (photoKey: string, comment: string | null) => {
    try {
      await photoService.updatePhoto(photoKey, { Comment: comment });
      await fetchPhotos();
    } catch (err) {
      setError((err as Error)?.message || 'Failed to update comment');
    }
  }, [fetchPhotos]);

  const handleDownloadAll = useCallback(async () => {
    try { await photoService.downloadZip(workOrderDetailKey); }
    catch (err) { setError((err as Error)?.message || 'Failed to download photos'); }
  }, [workOrderDetailKey]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      <Stack direction="row" alignItems="center" sx={{ mb: 2 }}>
        <Box sx={{ flex: 1 }}>
          <Typography variant="body2" color="text.secondary">
            Before/after photos per specimen — the report's photo page prints the first photo of each category.
          </Typography>
        </Box>
        <Button
          size="small"
          variant="outlined"
          startIcon={<Download fontSize="small" />}
          disabled={totalCount === 0}
          onClick={handleDownloadAll}
          sx={{ textTransform: 'none' }}
        >
          Download All ({totalCount})
        </Button>
      </Stack>

      {categories.map((cat) => (
        <TriaxialPhotoCategorySection
          key={cat.id}
          category={cat}
          photos={photosByCategory[cat.id]}
          uploading={uploadByCategory[cat.id] ?? null}
          onAddPhoto={(file) => handleAddPhoto(cat.id, file)}
          onRecrop={handleRecrop}
          onEditCropped={handleEditCropped}
          onDelete={handleDelete}
          onUpdateComment={handleUpdateComment}
          readOnly={!canEditTestData}
        />
      ))}

      {cropState && (
        <CropDialog
          open
          imageUrl={cropState.imageUrl}
          fileName={cropState.fileName}
          initialFullSelection={cropState.fullSelection}
          defaultAspect={BTS_REPORT_ASPECT}
          presets={BTS_DISC_ASPECT_PRESETS}
          onConfirm={handleCropConfirm}
          onCancel={handleCropCancel}
        />
      )}
    </Box>
  );
};
